
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import debounce from 'lodash/debounce';
  import {
    Client,
    NetworkModel,
    ServiceTypeModel,
    ClientSearchResultModel,
    IJobItemModel,
    JobItemModel
  } from '@/services/api.service';
  import vSelect from 'vue-select';
  import {
    JobItemType,
    Status,
    JobType,
    AreaType,
    EnumModel,
    StatusEnums,
    JobTypeEnums,
    JobItemTypeEnums,
    AreaTypeEnums
  } from '@/common/enumerations';
  import { EventBus } from '@/event-bus';
  import { JobItemUpdatedMessage } from '@/services/eventbus.models';
  import toISODateFormatter from '@/filters/toISOdate.formatter';
  import * as StringUtils from '@/utilities/string.utils';

  @Component({
    components: {
      vSelect
    },
    props: {
      jobItem: {
        type: Object as () => IJobItemModel,
        required: false
      },
      jobGid: String,
      jobIsQuoted: Boolean,
      isInDialogMode: Boolean
    },
    filters: {
      toISODate: toISODateFormatter
    }
  })
  export default class JobItemEdit extends Vue {
    private availableJobItemTypes: EnumModel[] = [];
    private availableStatusTypes: EnumModel[] = [];
    private gid: string = '';
    private availableServiceTypes: ServiceTypeModel[] = [];
    private filteredServiceTypes: ServiceTypeModel[] = [];

    private enableServiceTypeSelection: boolean = false;

    private name: string = '';
    private jobItemType: number = 0; // JobItemTypeEnums | null = null;
    private serviceTypeGid: string = '';
    private locationGid: string = '';
    private status: number = 0; // | null = null;
    private expectedStartDate: Date | undefined = undefined;
    private isFullDay: boolean = false;
    private purchaseOrderNumber: string = '';
    private notes: string = '';
    private taskContactName: string = '';
    private taskContactPhoneNumber: string = '';
    private quoteValue: number = 0;
    private isAdditionalToQuotedProjectScope: boolean = false;

    protected mounted() {
      // get the networks
      if (this.$props.jobItem) {
        this.gid = this.$props.jobItem.gid;
        this.name = this.$props.jobItem.name;
        // need to ensure the service type gid is set
        // prior to the jobItemType value, so the
        // filtering doesn't go to pot
        console.log('Mounting data');
        this.serviceTypeGid = this.$props.jobItem.serviceTypeGid;
        this.jobItemType = this.$props.jobItem.jobItemType;
        this.locationGid = this.$props.jobItem.locationGid;
        this.status = this.$props.jobItem.status;
        this.expectedStartDate = this.$props.jobItem.expectedStartDate as Date | undefined;
        this.isFullDay = this.$props.jobItem.isFullDay;
        this.purchaseOrderNumber = this.$props.jobItem.purchaseOrderNumber;
        this.notes = this.$props.jobItem.notes;
        this.taskContactName = this.$props.jobItem.contactName;
        this.taskContactPhoneNumber = this.$props.jobItem.contactPhoneNumber;
        this.quoteValue = this.$props.jobItem.quoteValue;
        this.isAdditionalToQuotedProjectScope = this.$props.jobItem.isAdditionalToQuotedProjectScope;
      }
      const pickJobItemTypeModel: EnumModel = new EnumModel('(Select Job Item Type...)', -1);
      this.availableJobItemTypes.push(
        ...JobItemTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableJobItemTypes.unshift(pickJobItemTypeModel);

      const pickStatusModel: EnumModel = new EnumModel('(Select Status...)', -1);
      this.availableStatusTypes.push(...StatusEnums);
      this.availableStatusTypes.unshift(pickStatusModel);
      this.fetchInitialData();
    }

    private filterServiceTypes(selectedJobItemType: number) {
      // restrict the list of the job item types to
      // only those that match the selected

      const currentServiceTypeGid = this.serviceTypeGid;
      if (selectedJobItemType === -1) {
        // the 'Select Job Item Type' option is selected
        this.filteredServiceTypes = [];
        const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
        pickServiceTypeModel.init({ name: '(Select Job Item Type First...)', gid: '' });
        this.filteredServiceTypes.push(pickServiceTypeModel);
        // force set the selected item back to the picker item
        this.serviceTypeGid = pickServiceTypeModel.gid || '';

        this.enableServiceTypeSelection = false;
      } else {
        // filter and reload the list
        let theFilteredList: ServiceTypeModel[] = [];
        theFilteredList = this.availableServiceTypes.filter(
          st => st.parentJobType === 0 || st.parentJobType === selectedJobItemType
        );
        // Inject the selector item if there are
        // multiple items to show, otherwise default to the
        // first one
        if (theFilteredList.length !== 1) {
          const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
          pickServiceTypeModel.init({ name: '(Select Service Type...)', gid: '' });
          theFilteredList.unshift(pickServiceTypeModel);
        }
        this.filteredServiceTypes = theFilteredList;
        if (this.filteredServiceTypes.filter(st => st.gid === currentServiceTypeGid)) {
          console.log('Filtered list contains previous service type: ' + currentServiceTypeGid);
          this.serviceTypeGid = currentServiceTypeGid;
        } else {
          console.log('Filtered list DOES NOT contains previous service type: ' + currentServiceTypeGid);
        }
        this.enableServiceTypeSelection = true;
      }
    }

    private updateJobItem() {
      console.log('updating job item');
      const me = this;
      const updateModel: IJobItemModel = {
        name: this.name,
        gid: this.gid,
        jobGid: this.$props.jobGid,
        locationGid: this.locationGid || undefined,
        jobItemType: this.jobItemType,
        serviceTypeGid: this.serviceTypeGid || undefined,
        status: this.status,
        expectedStartDate: this.expectedStartDate,
        isFullDay: this.isFullDay,
        purchaseOrderNumber: this.purchaseOrderNumber,
        notes: this.notes,
        contactName: this.taskContactName,
        contactPhoneNumber: this.taskContactPhoneNumber,
        quoteValue: this.quoteValue,
        isAdditionalToQuotedProjectScope: this.isAdditionalToQuotedProjectScope,
      };

      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      // @ts-ignore
      this.$Progress.start();

      clientService.updateJobItem(this.$props.jobGid, this.gid, new JobItemModel(updateModel)).then(data => {
        // Route to the edit client screen?
        // @ts-ignore
        this.$Progress.finish();
        // @ts-ignore
        this.$awn.success('Job item ' + me.name + ' updated successfully');
        this.$emit('job-item-updated', data);
        EventBus.$emit('job-item-updated', new JobItemUpdatedMessage(this.$props.jobGid, data));
        this.closeModal();
      });
    }
    private async fetchInitialData() {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);

      const [serviceTypes] = await Promise.all([
        // clientService.getNetworks(),
        clientService.getServiceTypes()
      ]);
      this.availableServiceTypes = serviceTypes.sort((n1, n2) =>
        StringUtils.stringCompare(n1.name || '', n2.name || '')
      );

      this.filterServiceTypes(this.jobItemType);

      // Now that all the data is loaded and available, we
      // can store the service type that was passed in, then
      // safely trigger the jobItemType value which will
      // execute the filter for the service types
      this.serviceTypeGid = this.$props.jobItem.serviceTypeGid;
      this.jobItemType = this.$props.jobItem.jobItemType;
    }

    private cancelEdit() {
      console.log('Cancelling edit...');
      // this.$emit('close');
      this.closeModal();
    }

    private closeModal() {
      if (this.$props.isInDialogMode) {
        this.$modal.hide('modal-jobitem-edit');
      }
    }

    @Watch('jobItemType')
    private onjobTypeChanged(value: number, oldValue: number) {
      console.log('Job Item Type Changed. Current service type is: ' + this.serviceTypeGid);
      this.filterServiceTypes(value);
    }
  }
